import {useEffect, useRef, useState} from 'react'
import {Typography, FormControlLabel, Checkbox, Box, ClickAwayListener} from '@mui/material'
import {ExploreLabel, StyledBadge, StyledButton} from './DashboardStyle'
import LanguageIcon from '@mui/icons-material/Language'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import {
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
  StyledArrowRightIcon,
  StyledBox,
  StyledBullet,
  StyledInfoBox,
  StyledLink,
  StyledMenuItem,
  StyledPaper,
  StyledPopper,
  StyledToolTip
} from './UseCaseStyles'
import {StyledUseCaseFormControlLabel} from '../../AdminPanel/ProjectDetails/UseCases/UseCaseStyles'
import {
  getInitialUseCaseConfig,
  getSelectedUseCases,
  handleUseCaseSelectionMaxLimitReached,
  isUseCasesSelectedByDomain,
  isUseCasesSelectedByTopics,
  handleUpdateFilters,
  isNoUseCases,
  hideUseCase,
  hideSubTopic,
  hideTopic,
  hideDomain
} from '../../AdminPanel/ProjectDetails/Helper/UseCaseHelper'
import {useFilters} from '../../store/hooks/filters'
import {useUseCases} from '../../store/hooks/useCase'
import {AnalyticsHandler} from '../../shared/handlers/analytics/handlers'
import {getUseCaseCustomLabel} from './helper'
import {useFilterListDetail} from '../../store/hooks/filterListDetail'
import {useRolePermissions} from '../../hooks/useRolesPermissions'
import {useFilterManagement} from '../../store/hooks/useFilterManagement'
import {selectPermission, selectPermissionLoading} from '../../store/slices/permissionSlice'
import {useSelector} from 'react-redux'
import {useConfig} from '../../store/hooks/config'

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />
const checkedIcon = <CheckBoxIcon fontSize='small' />

const UseCase = () => {
  const {useCaseList} = useFilterListDetail()

  const {useCases, setUseCases} = useUseCases()
  const [anchorEl, setAnchorEl] = useState(null)
  const [topicAnchorEl, setTopicAnchorEl] = useState(null)
  const [subTopicAnchorEl, setSubTopicAnchorEl] = useState(null)
  const [selectedTopics, setSelectedTopics] = useState([])
  const [selectedSubTopics, setSelectedSubTopics] = useState([])
  const [selectedData, setSelectedData] = useState({domain: '', topic: ''})
  const {isDeliveryAdmin} = useRolePermissions()
  const permission = useSelector(selectPermission)
  const permissionLoading = useSelector(selectPermissionLoading)

  // New filter management system
  const filterManagement = useFilterManagement()
  const {filterConfig} = filterManagement

  const {setFiltersOpen} = useFilters()
  const exploreByDomainBtnRef = useRef(null)
  const domainItemRef = useRef(null)
  const topicItemRef = useRef(null)
  const topicListRef = useRef(null)
  const domainListRef = useRef(null)
  const subTopicListRef = useRef(null)
  const {useCasesConfig} = useConfig() || {}

  useEffect(() => {
    const isAdmin = isDeliveryAdmin()
    const canProceed = isAdmin || !permissionLoading
    if (!canProceed || !useCasesConfig?.data || useCases.length !== 0) {
      return
    }
    const useCasePermissions = permission?.healthEquityConditionList?.usecasePermissions || []
    const permissionDetails = {isAdmin}
    const initialUseCases = getInitialUseCaseConfig(useCasesConfig.data, useCasePermissions, true, permissionDetails)
    setUseCases(initialUseCases)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useCasesConfig?.data, permissionLoading])

  const handleButtonClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
    setTopicAnchorEl(null)
    setSubTopicAnchorEl(null)
    setSelectedTopics([])
    setSelectedSubTopics([])
  }

  const handleDomainClick = (event, category) => {
    setTopicAnchorEl(event.currentTarget)
    setSubTopicAnchorEl(null)
    setSelectedTopics(category.topics)
    setSelectedData((prev) => ({...prev, domain: category.name, topic: ''}))
    setSelectedSubTopics([])
  }

  const handleTopicClick = (event, topic) => {
    setSubTopicAnchorEl(event.currentTarget)
    setSelectedSubTopics(topic.subTopics)
    setSelectedData((prev) => ({...prev, topic: topic.name}))
  }

  const handleClose = () => {
    setAnchorEl(null)
    setTopicAnchorEl(null)
    setSubTopicAnchorEl(null)
    setSelectedTopics([])
    setSelectedSubTopics([])
    setSelectedData({domain: '', topic: ''})
  }

  const updateUseCasesInSubTopic = (subTopic, targetSubTopicName, targetUseCaseName, isChecked) => {
    if (subTopic.name !== targetSubTopicName) return subTopic

    return {
      ...subTopic,
      useCases: subTopic.useCases.map((useCase) => {
        if (useCase.name === targetUseCaseName) {
          return {...useCase, selected: isChecked}
        }
        return useCase
      })
    }
  }

  const updateUseCasesInTopic = (topic, targetSubTopicName, targetUseCaseName, isChecked) => {
    return {
      ...topic,
      subTopics: topic.subTopics.map((subTopic) =>
        updateUseCasesInSubTopic(subTopic, targetSubTopicName, targetUseCaseName, isChecked)
      )
    }
  }

  const updateUseCasesInDomain = (domain, targetSubTopicName, targetUseCaseName, isChecked) => {
    return {
      ...domain,
      topics: domain.topics.map((topic) =>
        updateUseCasesInTopic(topic, targetSubTopicName, targetUseCaseName, isChecked)
      )
    }
  }

  const handleUseCaseSelection = (subTopicIndex, useCaseIndex) => (event) => {
    const isChecked = event.target.checked
    const targetSubTopicName = selectedSubTopics[subTopicIndex].name
    const targetUseCaseName = selectedSubTopics[subTopicIndex].useCases[useCaseIndex].name

    AnalyticsHandler.onUsecaseSelect({
      ...selectedData,
      subTopic: targetSubTopicName,
      name: targetUseCaseName
    })

    // Update the main useCases state with selection
    const updatedUseCases = useCases.map((domain) =>
      updateUseCasesInDomain(domain, targetSubTopicName, targetUseCaseName, isChecked)
    )

    const selectedUseCaseCount = getSelectedUseCases(updatedUseCases).length
    const limit = selectedUseCaseCount === 2 && isChecked ? 2 : selectedUseCaseCount
    const finalUseCases = handleUseCaseSelectionMaxLimitReached(updatedUseCases, limit)
    setUseCases(finalUseCases)

    // Sync selectedSubTopics and selectedTopics from finalUseCases to get disabled flags and update bullets
    const currentDomain = finalUseCases.find((d) => d.name === selectedData.domain)
    const currentTopic = currentDomain?.topics.find((t) => t.name === selectedData.topic)
    if (currentDomain?.topics) {
      setSelectedTopics(currentDomain.topics)
    }
    if (currentTopic?.subTopics) {
      setSelectedSubTopics(currentTopic.subTopics)
    }

    if (selectedUseCaseCount > 0) {
      setFiltersOpen(true)
    }

    // Use new filter management system
    const currentUseCaseFilters = selectedSubTopics[subTopicIndex].useCases[useCaseIndex].filters
    handleUpdateFilters(currentUseCaseFilters, finalUseCases, filterManagement, filterConfig)
  }

  const handleExploreButtonKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
    } else if (event.key === 'Enter') {
      event.preventDefault()
      setTimeout(() => domainItemRef.current?.focus(), 0)
      event.target.click()
    }
  }

  const handleMenuItemKeyDown = (event, index, listRef, childRef) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      const nextElement = listRef.current?.children[index + 1]
      nextElement?.focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      const prevElement = listRef.current?.children[index - 1]
      prevElement?.focus()
    } else if (event.key === 'Enter') {
      event.preventDefault()
      setTimeout(() => {
        childRef?.current?.focus()
      })
      event.target.click()
    } else if (event.key === 'Escape') {
      handleClose()
      exploreByDomainBtnRef.current?.focus()
    }
  }

  const handleUseCaseCheckboxKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.target.click()
    } else if (event.key === 'Escape') {
      handleClose()
    }
  }

  if (useCases?.length > 0 && isNoUseCases(useCases)) {
    return null
  }

  return (
    <>
      <ExploreLabel>OR</ExploreLabel>
      <ClickAwayListener onClickAway={handleClose}>
        <Box>
          <StyledBadge badgeContent={getSelectedUseCases(useCases)?.length} color='primary'>
            <StyledButton
              variant='subtitle1'
              onClick={handleButtonClick}
              onKeyDown={handleExploreButtonKeyDown}
              startIcon={<LanguageIcon />}
              aria-expanded={Boolean(anchorEl)}
              aria-controls='domain-menu'
              tabIndex={0}
              ref={exploreByDomainBtnRef}
            >
              &nbsp;Common Use Cases
            </StyledButton>
          </StyledBadge>

          <StyledPopper id='domain-menu' open={Boolean(anchorEl)} anchorEl={anchorEl} placement='bottom-start'>
            <StyledPaper domainPopper={true} ref={domainListRef}>
              {useCases.map((category, index) => {
                return hideDomain(category) ? null : (
                  <StyledMenuItem
                    highlight={selectedData.domain === category.name}
                    key={category.name}
                    onClick={(event) => handleDomainClick(event, category)}
                    onKeyDown={(event) => handleMenuItemKeyDown(event, index, domainListRef, topicItemRef)}
                    ref={index === 0 ? domainItemRef : null}
                    aria-label={`Domain: ${category.name}`}
                    aria-expanded={Boolean(topicAnchorEl)}
                    tabIndex={0}
                  >
                    <StyledBox>
                      {isUseCasesSelectedByDomain(category.topics) ? <StyledBullet /> : null}
                      {/* usecase category name here */}
                      <Box>{getUseCaseCustomLabel(useCaseList, category.name, 'domains')}</Box>
                    </StyledBox>
                    <StyledArrowRightIcon />
                  </StyledMenuItem>
                )
              })}
              <StyledInfoBox tabIndex={0}>
                Narrow down to domain specific data to simplify your workflow and start analysis. For more information
                and pricing details,{' '}
                <StyledLink href='mailto:ushealthprismsupport@deloitte.com'>contact us.</StyledLink>
              </StyledInfoBox>
            </StyledPaper>
          </StyledPopper>
          <StyledPopper open={Boolean(topicAnchorEl)} anchorEl={topicAnchorEl} placement='right-start'>
            <StyledPaper ref={topicListRef}>
              {selectedTopics.map((topic, index) => {
                return hideTopic(topic) ? null : (
                  <StyledMenuItem
                    highlight={selectedData.topic === topic.name}
                    key={topic.name}
                    onClick={(event) => handleTopicClick(event, topic)}
                    onKeyDown={(event) => handleMenuItemKeyDown(event, index, topicListRef, subTopicListRef)}
                    ref={index === 0 ? topicItemRef : null}
                    aria-label={`Topic: ${topic.name}`}
                    aria-expanded={Boolean(subTopicAnchorEl)}
                    tabIndex={0}
                  >
                    <StyledBox>
                      {isUseCasesSelectedByTopics(topic.subTopics) ? <StyledBullet /> : null}
                      {/* topic here */}
                      <Box>{getUseCaseCustomLabel(useCaseList, topic.name, 'topics')}</Box>
                    </StyledBox>
                    <StyledArrowRightIcon />
                  </StyledMenuItem>
                )
              })}
            </StyledPaper>
          </StyledPopper>
          <StyledPopper open={Boolean(subTopicAnchorEl)} anchorEl={subTopicAnchorEl} placement='right-start'>
            <StyledPaper ref={subTopicListRef} tabIndex={-1} aria-label='Sup Topics & Use Cases Section'>
              {selectedSubTopics.map((subTopic, subTopicIndex) => {
                return hideSubTopic(subTopic) ? null : (
                  <StyledAccordion
                    key={subTopic.name}
                    expanded={subTopic.expanded}
                    onChange={(isExpanded) => {
                      subTopic.expanded = isExpanded
                      selectedSubTopics[subTopicIndex].expanded = isExpanded
                      setSelectedSubTopics([...selectedSubTopics])
                    }}
                  >
                    <StyledAccordionSummary tabIndex={0} aria-label={`Sub Topic: ${subTopic.name}`}>
                      {/* subtopic here */}
                      <Typography>{getUseCaseCustomLabel(useCaseList, subTopic.name, 'subTopics')}</Typography>
                    </StyledAccordionSummary>
                    <StyledAccordionDetails>
                      {subTopic.useCases.map((useCase, useCaseIndex) => {
                        return hideUseCase(useCase) ? null : (
                          <StyledToolTip
                            key={useCase.id}
                            title={useCase.tooltipText}
                            placement={'bottom'}
                            PopperProps={{
                              modifiers: [
                                {
                                  name: 'offset',
                                  options: {
                                    offset: [0, -20]
                                  }
                                }
                              ]
                            }}
                          >
                            <Box>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    tabIndex={0}
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    checked={useCase.selected}
                                    disabled={useCase.disabled}
                                    onKeyDown={handleUseCaseCheckboxKeyDown}
                                    onClick={handleUseCaseSelection(subTopicIndex, useCaseIndex)}
                                  />
                                }
                                label={
                                  <StyledUseCaseFormControlLabel disabled={useCase.disabled}>
                                    {getUseCaseCustomLabel(useCaseList, useCase.name, 'useCases')}
                                  </StyledUseCaseFormControlLabel>
                                }
                                aria-label={`Use Case: ${useCase.name}`}
                              />
                            </Box>
                          </StyledToolTip>
                        )
                      })}
                    </StyledAccordionDetails>
                  </StyledAccordion>
                )
              })}
            </StyledPaper>
          </StyledPopper>
        </Box>
      </ClickAwayListener>
    </>
  )
}
export default UseCase

/* Funnction Descriptions :
  - useEffect: Initializes use cases based on user roles and permissions.
  - handleButtonClick: Toggles the domain menu visibility and resets selections.
  - handleDomainClick: Opens the topic menu and sets the selected domain.
  - handleTopicClick: Opens the sub-topic menu and sets the selected topic.
  - handleClose: Closes all menus and resets selections.
  - handleUseCaseSelection: Updates use case selection and applies related filters.
  - handleExploreButtonKeyDown: Handles keyboard navigation for the explore button.
  - handleMenuItemKeyDown: Handles keyboard navigation for menu items.
  - handleUseCaseCheckboxKeyDown: Handles keyboard navigation for use case checkboxes.
*/
